import { SafeImage } from './SafeImage';

const IMAGE_PATH = /\.(?:avif|gif|jpe?g|png|svg|webp)$/i;
const DIRECT_VIDEO_PATH = /\.(?:m3u8|mp4|webm)$/i;

type ResolvedMedia =
  | { kind: 'image'; src: string }
  | { kind: 'embed'; src: string }
  | { kind: 'video'; src: string };

export function CheckoutMedia({ src, className, title }: { src: string; className: string; title: string }) {
  const media = resolveCheckoutMedia(src);

  if (media.kind === 'embed') {
    return (
      <iframe
        className={className}
        src={media.src}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  }

  if (media.kind === 'video') {
    return (
      <video
        className={className}
        src={media.src}
        aria-label={title}
        controls
        playsInline
        preload="metadata"
      />
    );
  }

  return <SafeImage className={className} src={media.src} alt="" />;
}

export function resolveCheckoutMedia(source: string): ResolvedMedia {
  const value = source.trim();
  const url = parseHttpUrl(value);

  if (url === null) {
    return { kind: 'image', src: value };
  }

  if (IMAGE_PATH.test(url.pathname)) {
    return { kind: 'image', src: value };
  }

  const youtubeEmbed = youtubeEmbedUrl(url);
  if (youtubeEmbed !== null) {
    return { kind: 'embed', src: youtubeEmbed };
  }

  const vimeoEmbed = vimeoEmbedUrl(url);
  if (vimeoEmbed !== null) {
    return { kind: 'embed', src: vimeoEmbed };
  }

  const loomEmbed = loomEmbedUrl(url);
  if (loomEmbed !== null) {
    return { kind: 'embed', src: loomEmbed };
  }

  if (DIRECT_VIDEO_PATH.test(url.pathname)) {
    return { kind: 'video', src: value };
  }

  // The persisted contract has only mediaUrl. Uploaded images are normalized
  // to an image extension, so every other absolute HTTP(S) URL is treated as
  // an external player to support providers that are not cataloged here.
  return { kind: 'embed', src: value };
}

function parseHttpUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

function youtubeEmbedUrl(url: URL): string | null {
  const hostname = url.hostname.toLowerCase();
  const isShortHost = matchesHost(hostname, 'youtu.be');
  const isYoutubeHost = matchesHost(hostname, 'youtube.com') || matchesHost(hostname, 'youtube-nocookie.com');

  if (!isShortHost && !isYoutubeHost) {
    return null;
  }

  const segments = pathSegments(url);
  const firstSegment = segments[0]?.toLowerCase();
  const isWatchPath = url.pathname.replace(/\/+$/, '').toLowerCase() === '/watch';
  const videoId = isShortHost
    ? segments[0]
    : isWatchPath
      ? url.searchParams.get('v')
      : firstSegment === 'embed' || firstSegment === 'shorts' || firstSegment === 'live'
        ? segments[1]
        : undefined;

  if (videoId === undefined || videoId === null || !/^[a-zA-Z0-9_-]{6,}$/.test(videoId)) {
    return null;
  }

  const embedHost = matchesHost(hostname, 'youtube-nocookie.com')
    ? 'www.youtube-nocookie.com'
    : 'www.youtube.com';
  return `https://${embedHost}/embed/${videoId}`;
}

function vimeoEmbedUrl(url: URL): string | null {
  if (!matchesHost(url.hostname.toLowerCase(), 'vimeo.com')) {
    return null;
  }

  const videoId = pathSegments(url).find((segment) => /^\d+$/.test(segment));
  return videoId === undefined ? null : `https://player.vimeo.com/video/${videoId}`;
}

function loomEmbedUrl(url: URL): string | null {
  if (!matchesHost(url.hostname.toLowerCase(), 'loom.com')) {
    return null;
  }

  const segments = pathSegments(url);
  const markerIndex = segments.findIndex((segment) => {
    const normalized = segment.toLowerCase();
    return normalized === 'share' || normalized === 'embed';
  });
  const videoId = markerIndex >= 0 ? segments[markerIndex + 1] : undefined;
  return videoId === undefined ? null : `https://www.loom.com/embed/${videoId}`;
}

function pathSegments(url: URL): string[] {
  return url.pathname.split('/').filter(Boolean);
}

function matchesHost(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}
